<template>
  <div class="insurance-companies-page">
    <UkaskoLoader
      :is-loading="!isCompanyProductsReady && !isTextBlocksReady('promo') && !isTextBlocksReady('insurance-companies')"
    >
      <div class="insurance-companies">
        <AppHeader
          class="insurance-companies__header"
          :animation-enabled="false"
          :is-mobile-menu-open="isMobileMenuOpen"
        />

        <div class="insurance-companies__background">
          <BackgroundCirclesTop class="insurance-companies__background-circles" />
          <Advantage class="insurance-companies__advantage insurance-companies__advantage--key"><KeyImage /></Advantage>
          <Advantage class="insurance-companies__advantage insurance-companies__advantage--puzzle"><PuzzlePieceImage /></Advantage>
          <Advantage class="insurance-companies__advantage insurance-companies__advantage--laptop"><LaptopImage /></Advantage>
          <Advantage class="insurance-companies__advantage insurance-companies__advantage--bell"><BellImage /></Advantage>
          <Advantage class="insurance-companies__advantage insurance-companies__advantage--student"><StudentImage /></Advantage>
          <BackgroundGradientTop class="insurance-companies__background-gradient" />
        </div>
        <div class="insurance-companies__title-block">
          <h2 class="insurance-companies__title" v-html="replaceFancyText(getTitleBlock('insurance-companies', 'header', 'title'), 'insurance-companies')"></h2>
          <p class="insurance-companies__description" v-html="getTitleBlock('insurance-companies', 'header', 'description')"></p>
        </div>

        <div class="insurance-companies__content">
          <Accordion class="insurance-companies__accordion" v-for="company in getAllCompaniesWithProducts" :key="company.company_id">
            <template v-slot:title>
              <InsuranceCompanyLogo class="insurance-companies__info-item__logo" :logo="company.logo" :alternative-text="company.name" />
              <span class="insurance-companies__accordion-title">{{ company.name }}</span>
            </template>
            <ul class="insurance-companies__info-list">
              <li class="insurance-companies__info-item">
                <VioletFancyButton
                  class="insurance-companies__info-item__button"
                  :url="company.doc_company"
                  target="_blank"
                >
                  <span class="insurance-companies__info-item__button-text">Про страхову компанію</span>
                  <span class="insurance-companies__info-item__button-icon"><RightArrow /></span>
                </VioletFancyButton>
              </li>
              <li
                class="insurance-companies__info-item"
                v-for="(product, index) in company.products"
                :key="index"
              >
                <VioletFancyButton
                  class="insurance-companies__info-item__button"
                  :url="product.doc_product"
                  target="_blank"
                >
                  <span class="insurance-companies__info-item__button-text">Про {{ getProductNameById(product.product_code, 'capitalize') }}</span>
                  <span class="insurance-companies__info-item__button-icon"><RightArrow /></span>
                </VioletFancyButton>
              </li>
            </ul>
          </Accordion>
        </div>
        <div class="insurance-companies__footer"></div>
      </div>
      <div class="related-links">
        <div class="related-links__block">
          <div class="related-links__text-wrapper">
            <h4 class="related-links__title">Про страхового посередника</h4>
          </div>
          <VioletFancyButton class="related-links__button" :route="{ name: 'insurance-intermediary' }">
            <span class="related-links__button-text">Переглянути</span>
            <span class="related-links__button-icon"><RightArrow /></span>
          </VioletFancyButton>
        </div>
        <div class="related-links__footer"></div>
      </div>
      <AppFooter />
    </UkaskoLoader>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BackgroundGradientTop from './background-gradient-top';
import { textBlock } from '@/entities/text-block';
import { ukaskoFancyText } from '@/features/promo';
import { productsList } from '@/shared/lib/products';
import { Accordion } from '@/shared/ui/accordion';
import { VioletFancyButton } from '@/shared/ui/form';
import { RightArrow } from '@/shared/ui/icons';
import { InsuranceCompanyLogo } from '@/shared/ui/insurance-company-logo';
import {
  Advantage,
  BackgroundCirclesTop,
  BellImage,
  KeyImage,
  LaptopImage,
  PuzzlePieceImage,
  StudentImage
} from '@/shared/ui/promo';
import { UkaskoLoader } from '@/shared/ui/ukasko-loader';
import { AppFooter, AppHeader, promoMobileMenu } from '@/widgets/promo';

export default {
  created() {
    this.fetchCompanyProducts();
    this.fetchTextBlocks('promo');
    this.fetchTextBlocks('insurance-companies');
  },
  mixins: [ textBlock, ukaskoFancyText, productsList, promoMobileMenu ],
  components: {
    RightArrow, LaptopImage, Advantage, BackgroundCirclesTop, KeyImage, PuzzlePieceImage, AppFooter, AppHeader,
    UkaskoLoader, BackgroundGradientTop, Accordion, VioletFancyButton, BellImage, StudentImage, InsuranceCompanyLogo
  },
  computed: {
    ...mapGetters('textBlocksModule', ['isTextBlocksReady']),
    ...mapGetters('promoCompanyProductsModule', ['isCompanyProductsReady', 'getAllCompaniesWithProducts']),
  },
  methods: {
    ...mapActions('textBlocksModule', ['fetchTextBlocks']),
    ...mapActions('promoCompanyProductsModule', ['fetchCompanyProducts']),
  },
};
</script>

<style lang="scss" scoped>
.insurance-companies-page {
  background: linear-gradient(to bottom, #CADDFC 0px, #CADDFC 5px, rgba(202, 221, 252, 0.1) 100%);
}

.insurance-companies {
  position: relative;
  background-color: #CADDFC;
  padding-bottom: 95px;
  overflow: hidden;

  &__header {
    position: relative;
    z-index: 1;
  }

  &__background {
    position: relative;
  }

  &__background-circles {
    position: absolute;
    top: -291px;
    right: 0;
  }

  &__background-gradient {
    position: absolute;
    top: -270px;
    left: 50%;
    transform: translateX(-50%);
  }

  &__advantage {
    position: absolute;

    &--key {
      top: 0;
      left: 15%;
      opacity: 0.2;
    }

    &--puzzle {
      top: 90px;
      left: 6%;
      opacity: 0.2;

      @media (max-width: 600px) {
        opacity: 0;
      }
    }

    &--laptop {
      top: 325px;
      left: 7%;
      opacity: 0.3;

      @media (max-width: 800px) {
        opacity: 0;
      }
    }

    &--bell {
      top: 75px;
      left: 22%;
      opacity: 0.6;

      @media (max-width: 1180px) {
        opacity: 0;
      }
    }

    &--student {
      top: 370px;
      left: 24%;
      opacity: 0.2;
    }
  }

  &__title-block {
    position: relative;
    margin: 72px 12px 63px;

    @media (max-width: 700px) {
      margin-top: 40px;
    }
  }

  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 72px;
    font-weight: 500;
    color: #313437;
    text-align: center;
    margin-bottom: 16px;

    @media (max-width: 1050px) {
      font-size: 64px;
      margin-bottom: 16px;
    }

    @media (max-width: 800px) {
      font-size: 56px;
    }

    @media (max-width: 600px) {
      font-size: 44px;
    }

    @media (max-width: 500px) {
      font-size: 36px;
    }
  }

  &__description {
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: #494E53;
    text-align: center;

    @media (max-width: 500px) {
      font-size: 16px;
    }
  }

  &__content {
    position: relative;
    margin: 0 170px;

    @media (max-width: 1050px) {
      margin: 0 100px;
    }

    @media (max-width: 900px) {
      margin: 0 50px;
    }

    @media (max-width: 520px) {
      margin: 0 16px;
    }
  }

  &__accordion {
    @media (max-width: 720px) {
      margin-left: 16px;
      margin-right: 16px;
    }
  }

  &__accordion-title {
    font-family: 'Inter', sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #0C1421;

    @media (max-width: 1050px) {
      font-size: 32px;
    }

    @media (max-width: 700px) {
      font-size: 28px;
    }

    @media (max-width: 600px) {
      font-size: 24px;
    }
  }

  &__info-list {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  &__info-item {
    @media (max-width: 600px) {
      width: 100%;
    }

    &__button {
      margin-right: 24px;
      margin-bottom: 12px;

      @media (max-width: 600px) {
        width: 100%;

        ::v-deep .violet-fancy-button {
          padding: 8px !important;
          justify-content: space-between !important;
        }
      }
    }

    &__button-text {
      margin-right: 10px;
    }

    &__button-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 30px;
      height: 30px;
      background-color: #FFFFFF;
      border-radius: 50%;
    }
  }

  &__footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 32px;
    background-color: #FCFDFF;
    border-radius: 32px 32px 0 0;
  }
}

.related-links {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 95px 0 127px;
  background-color: #FCFDFF;
  overflow: hidden;

  @media (max-width: 1200px) {
    padding: 0 16px 64px;
  }

  @media (max-width: 800px) {
    flex-direction: column;
  }

  &__block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: rgba(255, 255, 255, 0.5);
    padding: 24px;
    margin-right: 32px;
    border-radius: 32px;
    backdrop-filter: blur(24px);
    box-shadow: 0 14px 32px 0 #9199AE1C;

    @media (max-width: 800px) {
      margin-right: 0;
      margin-bottom: 32px;
      z-index: 2;
    }

    @media (max-width: 600px) {
      padding: 24px 12px 24px 24px;
    }
  }

  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-weight: 500;
    font-size: 22px;
    color: #494E53;
    margin-right: 24px;
  }

  &__button {
    @media (max-width: 600px) {
      ::v-deep .violet-fancy-button {
        padding: 8px !important;
        justify-content: space-between !important;
      }
    }
  }

  &__button-text {
    @media (max-width: 600px) {
      display: none;
    }
  }

  &__button-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 30px;
    background-color: #FFFFFF;
    border-radius: 50%;
    margin-left: 10px;

    @media (max-width: 600px) {
      margin-left: 0;
    }
  }

  &__footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 32px;
    background-color: #313437;
    border-radius: 32px 32px 0 0;
  }
}

::v-deep .accordion__title {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

::v-deep .logo {
  max-height: 85px;
  max-width: 250px;
  margin-right: 32px;

  @media (max-width: 800px) {
    max-height: 60px;
    max-width: 178px;
  }

  @media (max-width: 600px) {
    max-height: 40px;
    max-width: 118px;
  }

  @media (max-width: 450px) {
    max-height: 25px;
    max-width: 70px;
  }
}

::v-deep .insurance-companies__fancy-text {
  background: linear-gradient(90deg, #4555CC 0%, #D75693 50%, #EB893C 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}
</style>
